/**
 * Supabase access for the HangarMax import tables, plus the share-link /
 * catalog-manifest proxy.
 *
 * The manifest and shared-scene JSON live on `cdn.hangarmax.com`, which sends
 * no CORS headers, so both go through the `hangarmax-share-import` edge
 * function rather than a direct browser fetch.
 */

import { supabase } from '@/lib/supabase'
import type {
  AircraftTypeCandidate,
  ExistingLink,
  HmItemCache,
  HmManifestItem,
  HmSceneData,
  ImportCounts,
  ImportSourceMeta,
  LinkStatus,
} from '@/lib/hangarmax-import/types'

// ----------------------------------------------------------------------------
// Sources
// ----------------------------------------------------------------------------

export interface ImportSourceRow {
  id: string
  organizationId: string
  locationId: string
  sourceKind: 'bundle_upload' | 'share_link'
  sourceLayoutId: string | null
  shareToken: string | null
  layoutName: string | null
  sceneHash: string | null
  counts: ImportCounts | null
  lastSyncedAt: string | null
  createdAt: string
  sceneData: HmSceneData | null
  itemCache: HmItemCache | null
}

interface RawSourceRow {
  id: string
  organization_id: string
  location_id: string
  source_kind: string
  source_layout_id: string | null
  share_token: string | null
  layout_name: string | null
  scene_hash: string | null
  counts: unknown
  last_synced_at: string | null
  created_at: string
  scene_data?: unknown
  item_cache?: unknown
}

function mapSourceRow(row: RawSourceRow): ImportSourceRow {
  return {
    id: row.id,
    organizationId: row.organization_id,
    locationId: row.location_id,
    sourceKind: row.source_kind === 'share_link' ? 'share_link' : 'bundle_upload',
    sourceLayoutId: row.source_layout_id,
    shareToken: row.share_token,
    layoutName: row.layout_name,
    sceneHash: row.scene_hash,
    counts: (row.counts as ImportCounts | null) ?? null,
    lastSyncedAt: row.last_synced_at,
    createdAt: row.created_at,
    sceneData: (row.scene_data as HmSceneData | undefined) ?? null,
    itemCache: (row.item_cache as HmItemCache | undefined) ?? null,
  }
}

const SOURCE_LIST_COLUMNS =
  'id, organization_id, location_id, source_kind, source_layout_id, share_token, layout_name, scene_hash, counts, last_synced_at, created_at'

export async function fetchImportSources(locationId: string): Promise<ImportSourceRow[]> {
  const { data, error } = await supabase
    .from('hangarmax_import_sources')
    .select(SOURCE_LIST_COLUMNS)
    .eq('location_id', locationId)
    .is('deleted_at', null)
    .order('created_at', { ascending: false })

  if (error) throw error
  return ((data ?? []) as unknown as RawSourceRow[]).map(mapSourceRow)
}

/** Full row including the preserved scene — used when re-syncing. */
export async function fetchImportSource(id: string): Promise<ImportSourceRow> {
  const { data, error } = await supabase
    .from('hangarmax_import_sources')
    .select(`${SOURCE_LIST_COLUMNS}, scene_data, item_cache`)
    .eq('id', id)
    .single()

  if (error) throw error
  return mapSourceRow(data as unknown as RawSourceRow)
}

/** Locate an existing source row by its HangarMax identity. */
export async function findImportSource(
  locationId: string,
  meta: Pick<ImportSourceMeta, 'sourceLayoutId' | 'shareToken'>
): Promise<ImportSourceRow | null> {
  let query = supabase
    .from('hangarmax_import_sources')
    .select(`${SOURCE_LIST_COLUMNS}, scene_data, item_cache`)
    .eq('location_id', locationId)
    .is('deleted_at', null)
    .limit(1)

  if (meta.sourceLayoutId) {
    query = query.eq('source_layout_id', meta.sourceLayoutId)
  } else if (meta.shareToken) {
    query = query.eq('share_token', meta.shareToken)
  } else {
    return null
  }

  const { data, error } = await query
  if (error) throw error
  const rows = (data ?? []) as unknown as RawSourceRow[]
  return rows.length > 0 ? mapSourceRow(rows[0]) : null
}

export interface UpsertSourceInput {
  id?: string | null
  organizationId: string
  locationId: string
  meta: ImportSourceMeta
  sceneData: HmSceneData
  itemCache: HmItemCache | null
  rawEnvelope: unknown
  catalogManifestSnapshot: HmManifestItem[] | null
  sceneHash: string
  anchor: unknown
}

export async function upsertImportSource(input: UpsertSourceInput): Promise<string> {
  const payload = {
    organization_id: input.organizationId,
    location_id: input.locationId,
    source_kind: input.meta.sourceKind,
    source_layout_id: input.meta.sourceLayoutId,
    share_token: input.meta.shareToken,
    source_project_ref: input.meta.sourceProjectRef,
    source_export_version: input.meta.sourceExportVersion,
    layout_name: input.meta.layoutName,
    scene_data: input.sceneData,
    item_cache: input.itemCache,
    raw_envelope: input.rawEnvelope,
    catalog_manifest_snapshot: input.catalogManifestSnapshot,
    scene_hash: input.sceneHash,
    anchor: input.anchor,
  }

  if (input.id) {
    const { data, error } = await supabase
      .from('hangarmax_import_sources')
      .update(payload)
      .eq('id', input.id)
      .select('id')
      .single()
    if (error) throw error
    return (data as { id: string }).id
  }

  const { data, error } = await supabase
    .from('hangarmax_import_sources')
    .insert(payload)
    .select('id')
    .single()
  if (error) throw error
  return (data as { id: string }).id
}

export async function finalizeImportSource(
  id: string,
  counts: ImportCounts
): Promise<void> {
  const { error } = await supabase
    .from('hangarmax_import_sources')
    .update({ counts, last_synced_at: new Date().toISOString() })
    .eq('id', id)

  if (error) throw error
}

export async function deleteImportSource(id: string): Promise<void> {
  const { error } = await supabase
    .from('hangarmax_import_sources')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id)

  if (error) throw error
}

// ----------------------------------------------------------------------------
// Links
// ----------------------------------------------------------------------------

interface RawLinkRow {
  id: string
  source_object_type: string
  source_object_id: string
  source_hash: string
  status: string
  status_detail: string | null
  target_table: string | null
  target_id: string | null
  target_refs: unknown
  catalog_id: string | null
  match_info: unknown
}

export interface ImportLinkRow extends ExistingLink {
  statusDetail: string | null
  catalogId: string | null
  matchInfo: Record<string, unknown> | null
}

export async function fetchImportLinks(sourceId: string): Promise<ImportLinkRow[]> {
  const { data, error } = await supabase
    .from('hangarmax_import_links')
    .select(
      'id, source_object_type, source_object_id, source_hash, status, status_detail, target_table, target_id, target_refs, catalog_id, match_info'
    )
    .eq('source_id', sourceId)
    .is('deleted_at', null)

  if (error) throw error

  return ((data ?? []) as unknown as RawLinkRow[]).map((row) => ({
    id: row.id,
    sourceObjectType: row.source_object_type === 'building' ? 'building' : 'item',
    sourceObjectId: row.source_object_id,
    sourceHash: row.source_hash,
    status: row.status as LinkStatus,
    statusDetail: row.status_detail,
    targetTable: row.target_table,
    targetId: row.target_id,
    targetRefs: (row.target_refs as Record<string, unknown> | null) ?? null,
    catalogId: row.catalog_id,
    matchInfo: (row.match_info as Record<string, unknown> | null) ?? null,
  }))
}

export interface UpsertLinkInput {
  id?: string | null
  organizationId: string
  sourceId: string
  sourceObjectType: 'building' | 'item'
  sourceObjectId: string
  catalogId: string | null
  targetTable: 'areas' | 'aircraft' | 'obstacles' | null
  targetId: string | null
  targetRefs: Record<string, unknown> | null
  status: LinkStatus
  statusDetail: string | null
  matchInfo: Record<string, unknown> | null
  rawSource: unknown
  sourceHash: string
  firstImport: boolean
}

export async function upsertImportLink(input: UpsertLinkInput): Promise<string> {
  const now = new Date().toISOString()
  const payload: Record<string, unknown> = {
    organization_id: input.organizationId,
    source_id: input.sourceId,
    source_object_type: input.sourceObjectType,
    source_object_id: input.sourceObjectId,
    catalog_id: input.catalogId,
    target_table: input.targetTable,
    target_id: input.targetId,
    target_refs: input.targetRefs,
    status: input.status,
    status_detail: input.statusDetail,
    match_info: input.matchInfo,
    raw_source: input.rawSource,
    source_hash: input.sourceHash,
    last_synced_at: now,
  }
  if (input.firstImport) payload.first_imported_at = now

  // `UNIQUE (source_id, source_object_type, source_object_id)` makes this a
  // natural upsert, so a re-run after a partial failure converges rather than
  // erroring on a duplicate.
  const { data, error } = await supabase
    .from('hangarmax_import_links')
    .upsert(payload, { onConflict: 'source_id,source_object_type,source_object_id' })
    .select('id')
    .single()

  if (error) throw error
  return (data as { id: string }).id
}

export async function markLinkSourceDeleted(id: string): Promise<void> {
  const { error } = await supabase
    .from('hangarmax_import_links')
    .update({ status: 'source_deleted', status_detail: 'Removed from the HangarMax layout', last_synced_at: new Date().toISOString() })
    .eq('id', id)

  if (error) throw error
}

// ----------------------------------------------------------------------------
// Aircraft type candidates
// ----------------------------------------------------------------------------

export async function fetchAircraftTypeCandidates(): Promise<AircraftTypeCandidate[]> {
  const { data, error } = await supabase
    .from('aircraft_types')
    .select('id, type_code, icao_designator, manufacturer, model, wingspan, length, height')

  if (error) throw error

  return (data ?? []).map((row) => ({
    id: row.id,
    typeCode: row.type_code,
    icaoDesignator: row.icao_designator,
    manufacturer: row.manufacturer,
    model: row.model,
    wingspan: row.wingspan,
    length: row.length,
    height: row.height,
  }))
}

/** Ids of rows still present, so the planner can spot user-deleted targets. */
export async function fetchExistingTargetIds(
  links: Array<{ targetTable: string | null; targetId: string | null }>
): Promise<Set<string>> {
  const byTable: Record<string, string[]> = {}
  for (const link of links) {
    if (!link.targetTable || !link.targetId) continue
    ;(byTable[link.targetTable] ??= []).push(link.targetId)
  }

  const alive = new Set<string>()

  for (const [table, ids] of Object.entries(byTable)) {
    if (ids.length === 0) continue
    const { data, error } = await supabase
      .from(table as 'areas' | 'aircraft' | 'obstacles')
      .select('id')
      .in('id', ids)
      .is('deleted_at', null)

    if (error) throw error
    for (const row of (data ?? []) as Array<{ id: string }>) alive.add(row.id)
  }

  return alive
}

// ----------------------------------------------------------------------------
// Share link / catalog proxy (edge function)
// ----------------------------------------------------------------------------

export interface ShareFetchResult {
  sceneData: HmSceneData
  catalogEntries: HmManifestItem[]
  shareToken: string
  layoutName: string | null
}

/** Fetch a published HangarMax layout by share URL or bare token. */
export async function fetchSharedLayout(urlOrToken: string): Promise<ShareFetchResult> {
  const { data, error } = await supabase.functions.invoke('hangarmax-share-import', {
    body: { mode: 'share', url_or_token: urlOrToken },
  })

  if (error) throw new Error(readableFunctionError(error, 'Could not fetch that HangarMax share link.'))
  const payload = data as {
    scene_data?: HmSceneData
    catalog_entries?: HmManifestItem[]
    share_token?: string
    layout_name?: string | null
    error?: string
  }
  if (payload?.error) throw new Error(payload.error)
  if (!payload?.scene_data) throw new Error('The share link returned no layout data.')

  return {
    sceneData: payload.scene_data,
    catalogEntries: payload.catalog_entries ?? [],
    shareToken: payload.share_token ?? urlOrToken,
    layoutName: payload.layout_name ?? null,
  }
}

/**
 * Resolve catalog ids to manifest entries (names + dimensions).
 *
 * Returns an empty array rather than throwing: a bundle import still works
 * without the manifest, just with weaker aircraft-type matching, and the plan
 * surfaces that as a warning.
 */
export async function fetchCatalogEntries(catalogIds: string[]): Promise<HmManifestItem[]> {
  if (catalogIds.length === 0) return []

  try {
    const { data, error } = await supabase.functions.invoke('hangarmax-share-import', {
      body: { mode: 'catalog', catalog_ids: catalogIds },
    })
    if (error) return []
    return ((data as { catalog_entries?: HmManifestItem[] })?.catalog_entries ?? [])
  } catch {
    return []
  }
}

function readableFunctionError(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'message' in error) {
    const message = String((error as { message: unknown }).message)
    if (message) return message
  }
  return fallback
}
